import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../services/api';
import SearchBar from '../components/SearchBar';
import FilterDropdown from '../components/FilterDropdown';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { exportToCSV, downloadTemplate, formatDate, getStatusColor, getStatusLabel } from '../utils/export';

const DashboardBCEIP = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ companies: 0, declarations: 0, pending: 0, users: 0 });
  const [companies, setCompanies] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [declarations, setDeclarations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Search and filter states
  const [companySearch, setCompanySearch] = useState('');
  const [companySector, setCompanySector] = useState('');
  const [companyStatus, setCompanyStatus] = useState('');
  
  const [userSearch, setUserSearch] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userStatus, setUserStatus] = useState('');
  
  const [declSearch, setDeclSearch] = useState('');
  const [declStatus, setDeclStatus] = useState('');
  const [declYear, setDeclYear] = useState('');
  
  // Pagination states
  const [companyPage, setCompanyPage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const [declPage, setDeclPage] = useState(1);
  const itemsPerPage = 10;
  
  // Modal states
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any>(null);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [viewingDeclaration, setViewingDeclaration] = useState<any>(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Form states
  const [newCompany, setNewCompany] = useState({
    name: '', nif: '', sector: '', address: '', phone: '', email: '',
    legal_form: 'SA', registration_number: '', employee_count: ''
  });
  
  const [newUser, setNewUser] = useState({
    tenant_id: '', email: '', first_name: '', last_name: '', role: 'admin', password: ''
  });

  // Logout handler
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch data when tab changes
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Main data fetching function
  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'companies' || activeTab === 'overview') {
        const companiesRes = await api.get('/api/companies');
        if (companiesRes.data.success) {
          setCompanies(companiesRes.data.companies || []);
          setStats(prev => ({ ...prev, companies: companiesRes.data.companies?.length || 0 }));
        }
      }
      if (activeTab === 'users' || activeTab === 'overview') {
        const usersRes = await api.get('/api/users');
        if (usersRes.data.success) {
          setUsers(usersRes.data.users || []);
          setStats(prev => ({ ...prev, users: usersRes.data.users?.length || 0 }));
        }
      }
      if (activeTab === 'declarations' || activeTab === 'overview') {
        const declRes = await api.get('/api/declarations');
        if (declRes.data.success) {
          setDeclarations(declRes.data.declarations || []);
          const pending = (declRes.data.declarations || []).filter((d: any) => d.status === 'pending').length;
          setStats(prev => ({ 
            ...prev, 
            declarations: declRes.data.declarations?.length || 0,
            pending 
          }));
        }
      }
    } catch (error: any) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  // Company CRUD operations
  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/companies', newCompany);
      if (response.data.success) {
        toast.success('Entreprise créée avec succès!');
        setShowCompanyModal(false);
        setNewCompany({
          name: '', nif: '', sector: '', address: '', phone: '', email: '',
          legal_form: 'SA', registration_number: '', employee_count: ''
        });
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec de création');
    }
  };

  const handleEditCompany = (company: any) => {
    setEditingCompany(company);
    setNewCompany({
      name: company.name,
      nif: company.nif,
      sector: company.sector,
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      legal_form: company.legal_form || 'SA',
      registration_number: company.registration_number || '',
      employee_count: company.employee_count || ''
    });
    setShowEditCompanyModal(true);
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCompany) return;
    
    try {
      const response = await api.put(`/api/companies/${editingCompany.id}`, newCompany);
      if (response.data.success) {
        toast.success('Entreprise mise à jour avec succès!');
        setShowEditCompanyModal(false);
        setEditingCompany(null);
        setNewCompany({
          name: '', nif: '', sector: '', address: '', phone: '', email: '',
          legal_form: 'SA', registration_number: '', employee_count: ''
        });
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec de mise à jour');
    }
  };

  const handleDeleteCompany = async (id: number, name: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'entreprise "${name}" ?`)) return;
    
    try {
      const response = await api.delete(`/api/companies/${id}`);
      if (response.data.success) {
        toast.success('Entreprise supprimée avec succès!');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec de suppression');
    }
  };

  // User CRUD operations
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/users', newUser);
      if (response.data.success) {
        toast.success('Utilisateur créé avec succès!');
        setShowUserModal(false);
        setNewUser({
          tenant_id: '', email: '', first_name: '', last_name: '', role: 'admin', password: ''
        });
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec de création');
    }
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setNewUser({
      tenant_id: user.tenant_id,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      password: ''
    });
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    try {
      const updateData = { ...newUser };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const response = await api.put(`/api/users/${editingUser.id}`, updateData);
      if (response.data.success) {
        toast.success('Utilisateur mis à jour avec succès!');
        setShowEditUserModal(false);
        setEditingUser(null);
        setNewUser({
          tenant_id: '', email: '', first_name: '', last_name: '', role: 'admin', password: ''
        });
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec de mise à jour');
    }
  };

  const handleDeleteUser = async (id: number, name: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${name}" ?`)) return;
    
    try {
      const response = await api.delete(`/api/users/${id}`);
      if (response.data.success) {
        toast.success('Utilisateur supprimé avec succès!');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec de suppression');
    }
  };

  // Declaration operations
  const handleApproveDeclaration = async (id: number) => {
    if (!window.confirm('Approuver cette déclaration?')) return;
    
    try {
      const response = await api.put(`/api/declarations/${id}`, { status: 'validated' });
      if (response.data.success) {
        toast.success('Déclaration approuvée!');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec');
    }
  };

  const handleRejectDeclaration = async (id: number) => {
    const reason = window.prompt('Raison du rejet:');
    if (!reason) return;
    
    try {
      const response = await api.put(`/api/declarations/${id}`, { 
        status: 'rejected', 
        rejection_reason: reason 
      });
      if (response.data.success) {
        toast.success('Déclaration rejetée!');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Échec');
    }
  };

  const handleViewDeclaration = (decl: any) => {
    setViewingDeclaration(decl);
    setShowDeclarationModal(true);
  };

  const handleGeneratePDF = async (decl: any) => {
    setGeneratingPDF(true);
    try {
      const response = await api.get(`/api/declarations/${decl.id}/pdf`, {
        responseType: 'text'
      });
      
      // Open HTML in new window so user can print to PDF
      const htmlContent = response.data;
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        
        // Auto-trigger print dialog after a short delay
        setTimeout(() => {
          newWindow.print();
        }, 500);
        
        toast.success('Bilan ouvert dans une nouvelle fenêtre. Utilisez Ctrl+P ou Cmd+P pour enregistrer en PDF.');
      } else {
        toast.error('Veuillez autoriser les pop-ups pour télécharger le PDF');
      }
    } catch (error: any) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Export functions
  const handleExportCompanies = async () => {
    setExporting(true);
    try {
      let endpoint = '/api/companies/export?';
      if (companySearch) endpoint += `search=${encodeURIComponent(companySearch)}&`;
      if (companySector) endpoint += `sector=${encodeURIComponent(companySector)}&`;
      if (companyStatus) endpoint += `status=${companyStatus}&`;
      
      await exportToCSV(endpoint, `entreprises_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Export réussi!');
    } catch (error: any) {
      toast.error(error.message || 'Échec de l\'export');
    }
    setExporting(false);
  };

  const handleExportUsers = async () => {
    setExporting(true);
    try {
      let endpoint = '/api/users/export?';
      if (userSearch) endpoint += `search=${encodeURIComponent(userSearch)}&`;
      if (userRole) endpoint += `role=${userRole}&`;
      if (userStatus) endpoint += `status=${userStatus}&`;
      
      await exportToCSV(endpoint, `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Export réussi!');
    } catch (error: any) {
      toast.error(error.message || 'Échec de l\'export');
    }
    setExporting(false);
  };

  const handleExportDeclarations = async () => {
    setExporting(true);
    try {
      let endpoint = '/api/declarations/export?';
      if (declSearch) endpoint += `search=${encodeURIComponent(declSearch)}&`;
      if (declStatus) endpoint += `status=${declStatus}&`;
      if (declYear) endpoint += `year=${declYear}&`;
      
      await exportToCSV(endpoint, `declarations_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Export réussi!');
    } catch (error: any) {
      toast.error(error.message || 'Échec de l\'export');
    }
    setExporting(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
      toast.success('Modèle téléchargé avec succès!');
    } catch (error: any) {
      toast.error('Échec du téléchargement du modèle');
    }
  };

  // Filter data based on search and filters
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !companySearch || 
      company.name?.toLowerCase().includes(companySearch.toLowerCase()) ||
      company.nif?.toLowerCase().includes(companySearch.toLowerCase());
    const matchesSector = !companySector || company.sector === companySector;
    const matchesStatus = !companyStatus || company.status === companyStatus;
    return matchesSearch && matchesSector && matchesStatus;
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = !userSearch || 
      user.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesRole = !userRole || user.role === userRole;
    const matchesStatus = !userStatus || user.status === userStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const filteredDeclarations = declarations.filter(decl => {
    const matchesSearch = !declSearch || 
      decl.year?.toString().includes(declSearch);
    const matchesStatus = !declStatus || decl.status === declStatus;
    const matchesYear = !declYear || decl.year?.toString() === declYear;
    return matchesSearch && matchesStatus && matchesYear;
  });

  // Pagination calculations
  const paginatedCompanies = filteredCompanies.slice(
    (companyPage - 1) * itemsPerPage,
    companyPage * itemsPerPage
  );
  const companyTotalPages = Math.ceil(filteredCompanies.length / itemsPerPage);

  const paginatedUsers = filteredUsers.slice(
    (userPage - 1) * itemsPerPage,
    userPage * itemsPerPage
  );
  const userTotalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const paginatedDeclarations = filteredDeclarations.slice(
    (declPage - 1) * itemsPerPage,
    declPage * itemsPerPage
  );
  const declTotalPages = Math.ceil(filteredDeclarations.length / itemsPerPage);

  // Get company name by ID
  const getCompanyName = (tenantId: number) => {
    const company = companies.find(c => c.id === tenantId);
    return company?.name || 'N/A';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <nav className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <img src="/logo.svg" alt="Logo" className="h-10 w-auto" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center">
                  Portail de déclaration Bilan Social
                </h1>
                <p className="text-sm text-blue-100">République de Guinée - BCEIP</p>
              </div>
            </div>
            
            {/* User Info and Logout */}
            <div className="flex items-center space-x-4">
              <div className="text-right hidden md:block">
                <p className="text-sm text-white font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-blue-100">Super Administrateur</p>
              </div>
              <button 
                onClick={handleLogout} 
                className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition font-medium shadow-md"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">
            <i className="fas fa-user-shield mr-2"></i>
            Bienvenue, {user?.first_name} {user?.last_name}
          </h2>
          <p className="text-gray-600">Rôle : {user?.role?.toUpperCase()}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <i className="fas fa-building text-2xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Entreprises</h3>
                <p className="text-3xl font-bold text-green-600">{stats.companies}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-3">Total enregistrées</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                <i className="fas fa-users text-2xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Utilisateurs</h3>
                <p className="text-3xl font-bold text-purple-600">{stats.users}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-3">Total actifs</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                <i className="fas fa-file-alt text-2xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Déclarations</h3>
                <p className="text-3xl font-bold text-blue-600">{stats.declarations}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-3">Total reçues</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
                <i className="fas fa-clock text-2xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">En attente</h3>
                <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-3">Validation requise</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b">
            <div className="flex overflow-x-auto">
              {[
                { key: 'overview', label: 'Vue d\'ensemble', icon: 'fa-th-large' },
                { key: 'companies', label: 'Entreprises', icon: 'fa-building' },
                { key: 'users', label: 'Utilisateurs', icon: 'fa-users' },
                { key: 'declarations', label: 'Déclarations', icon: 'fa-file-alt' },
                { key: 'reports', label: 'Rapports', icon: 'fa-chart-bar' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center py-4 px-6 font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.key 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <i className={`fas ${tab.icon} mr-2`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <LoadingSpinner text="Chargement des données..." />
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-6">
                      <i className="fas fa-chart-pie mr-2"></i>
                      Tableau de bord
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <i className="fas fa-building mr-2 text-green-600"></i>
                          Entreprises récentes
                        </h4>
                        {companies.slice(0, 5).length > 0 ? (
                          companies.slice(0, 5).map((company: any) => (
                            <div key={company.id} className="py-2 border-b last:border-b-0">
                              <p className="font-medium">{company.name}</p>
                              <p className="text-sm text-gray-600">{company.sector}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">Aucune entreprise</p>
                        )}
                      </div>
                      
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3 flex items-center">
                          <i className="fas fa-clock mr-2 text-orange-600"></i>
                          Déclarations en attente
                        </h4>
                        {declarations.filter((d: any) => d.status === 'pending').slice(0, 5).length > 0 ? (
                          declarations.filter((d: any) => d.status === 'pending').slice(0, 5).map((decl: any) => (
                            <div key={decl.id} className="py-2 border-b last:border-b-0">
                              <p className="font-medium">Année {decl.year}</p>
                              <p className="text-sm text-gray-600">
                                {getCompanyName(decl.tenant_id)} - {formatDate(decl.submitted_at || decl.created_at)}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">Aucune déclaration en attente</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Companies Tab */}
                {activeTab === 'companies' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold">
                        <i className="fas fa-building mr-2"></i>
                        Gestion des Entreprises
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDownloadTemplate}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                        >
                          <i className="fas fa-download mr-2"></i>
                          Modèle CSV
                        </button>
                        <button
                          onClick={handleExportCompanies}
                          disabled={exporting}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                          <i className="fas fa-file-export mr-2"></i>
                          {exporting ? 'Export...' : 'Exporter'}
                        </button>
                        <button
                          onClick={() => setShowCompanyModal(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                          <i className="fas fa-plus mr-2"></i>
                          Ajouter
                        </button>
                      </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <SearchBar
                        value={companySearch}
                        onChange={setCompanySearch}
                        placeholder="Rechercher par nom ou NIF..."
                        className="md:col-span-2"
                      />
                      <FilterDropdown
                        label="Secteur"
                        value={companySector}
                        onChange={setCompanySector}
                        options={[
                          { label: 'Mines', value: 'Mines' },
                          { label: 'Agriculture', value: 'Agriculture' },
                          { label: 'Industrie', value: 'Industrie' },
                          { label: 'Services', value: 'Services' },
                          { label: 'Commerce', value: 'Commerce' },
                          { label: 'BTP', value: 'BTP' },
                          { label: 'Télécommunications', value: 'Télécommunications' }
                        ]}
                      />
                      <FilterDropdown
                        label="Statut"
                        value={companyStatus}
                        onChange={setCompanyStatus}
                        options={[
                          { label: 'Actif', value: 'active' },
                          { label: 'Inactif', value: 'inactive' },
                          { label: 'Suspendu', value: 'suspended' }
                        ]}
                      />
                    </div>

                    {/* Companies Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NIF</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Secteur</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employés</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedCompanies.length > 0 ? (
                            paginatedCompanies.map((company: any) => (
                              <tr key={company.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="font-medium text-gray-900">{company.name}</div>
                                  <div className="text-sm text-gray-500">{company.email}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">{company.nif}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{company.sector}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{company.employee_count || 'N/A'}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(company.status)}`}>
                                    {getStatusLabel(company.status)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm font-medium">
                                  <button
                                    onClick={() => handleEditCompany(company)}
                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCompany(company.id, company.name)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                                Aucune entreprise trouvée
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                      currentPage={companyPage}
                      totalPages={companyTotalPages}
                      onPageChange={setCompanyPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredCompanies.length}
                    />
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold">
                        <i className="fas fa-users mr-2"></i>
                        Gestion des Utilisateurs
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={handleExportUsers}
                          disabled={exporting}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                          <i className="fas fa-file-export mr-2"></i>
                          {exporting ? 'Export...' : 'Exporter'}
                        </button>
                        <button
                          onClick={() => setShowUserModal(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                          <i className="fas fa-plus mr-2"></i>
                          Ajouter
                        </button>
                      </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <SearchBar
                        value={userSearch}
                        onChange={setUserSearch}
                        placeholder="Rechercher par nom ou email..."
                        className="md:col-span-2"
                      />
                      <FilterDropdown
                        label="Rôle"
                        value={userRole}
                        onChange={setUserRole}
                        options={[
                          { label: 'Admin', value: 'admin' },
                          { label: 'Contributeur', value: 'contributor' },
                          { label: 'Lecteur', value: 'reader' },
                          { label: 'Super Admin', value: 'super_admin' }
                        ]}
                      />
                      <FilterDropdown
                        label="Statut"
                        value={userStatus}
                        onChange={setUserStatus}
                        options={[
                          { label: 'Actif', value: 'active' },
                          { label: 'Inactif', value: 'inactive' },
                          { label: 'Suspendu', value: 'suspended' }
                        ]}
                      />
                    </div>

                    {/* Users Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedUsers.length > 0 ? (
                            paginatedUsers.map((user: any) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="font-medium text-gray-900">{user.first_name} {user.last_name}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {getCompanyName(user.tenant_id)}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">{user.role?.toUpperCase()}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
                                    {getStatusLabel(user.status)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm font-medium">
                                  <button
                                    onClick={() => handleEditUser(user)}
                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                                Aucun utilisateur trouvé
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                      currentPage={userPage}
                      totalPages={userTotalPages}
                      onPageChange={setUserPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredUsers.length}
                    />
                  </div>
                )}

                {/* Declarations Tab */}
                {activeTab === 'declarations' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold">
                        <i className="fas fa-file-alt mr-2"></i>
                        Gestion des Déclarations
                      </h3>
                      <button
                        onClick={handleExportDeclarations}
                        disabled={exporting}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                      >
                        <i className="fas fa-file-export mr-2"></i>
                        {exporting ? 'Export...' : 'Exporter'}
                      </button>
                    </div>

                    {/* Search and Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <SearchBar
                        value={declSearch}
                        onChange={setDeclSearch}
                        placeholder="Rechercher par année..."
                      />
                      <FilterDropdown
                        label="Statut"
                        value={declStatus}
                        onChange={setDeclStatus}
                        options={[
                          { label: 'Brouillon', value: 'draft' },
                          { label: 'En attente', value: 'pending' },
                          { label: 'Validée', value: 'validated' },
                          { label: 'Rejetée', value: 'rejected' }
                        ]}
                      />
                      <FilterDropdown
                        label="Année"
                        value={declYear}
                        onChange={setDeclYear}
                        options={[
                          { label: '2025', value: '2025' },
                          { label: '2024', value: '2024' },
                          { label: '2023', value: '2023' },
                          { label: '2022', value: '2022' }
                        ]}
                      />
                    </div>

                    {/* Declarations Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employés</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedDeclarations.length > 0 ? (
                            paginatedDeclarations.map((decl: any) => (
                              <tr key={decl.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 font-medium text-gray-900">{decl.year}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {getCompanyName(decl.tenant_id)}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {(decl.permanent_employees || 0) + (decl.temporary_employees || 0)}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(decl.status)}`}>
                                    {getStatusLabel(decl.status)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                  {formatDate(decl.submitted_at || decl.created_at)}
                                </td>
                                <td className="px-6 py-4 text-sm font-medium">
                                  <button
                                    onClick={() => handleViewDeclaration(decl)}
                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                    title="Voir les détails"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                  <button
                                    onClick={() => handleGeneratePDF(decl)}
                                    className="text-purple-600 hover:text-purple-900 mr-3"
                                    title="Télécharger le bilan PDF"
                                  >
                                    <i className="fas fa-file-pdf"></i>
                                  </button>
                                  {decl.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() => handleApproveDeclaration(decl.id)}
                                        className="text-green-600 hover:text-green-900 mr-3"
                                        title="Approuver"
                                      >
                                        <i className="fas fa-check-circle"></i>
                                      </button>
                                      <button
                                        onClick={() => handleRejectDeclaration(decl.id)}
                                        className="text-red-600 hover:text-red-900"
                                        title="Rejeter"
                                      >
                                        <i className="fas fa-times-circle"></i>
                                      </button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                                Aucune déclaration trouvée
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                      currentPage={declPage}
                      totalPages={declTotalPages}
                      onPageChange={setDeclPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredDeclarations.length}
                    />
                  </div>
                )}

                {/* Reports Tab */}
                {activeTab === 'reports' && (
                  <div>
                    <h3 className="text-xl font-semibold mb-6">
                      <i className="fas fa-chart-bar mr-2"></i>
                      Rapports et Statistiques
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border rounded-lg p-6">
                        <h4 className="font-semibold mb-4 flex items-center">
                          <i className="fas fa-download mr-2 text-blue-600"></i>
                          Exports disponibles
                        </h4>
                        <div className="space-y-3">
                          <button
                            onClick={handleExportCompanies}
                            disabled={exporting}
                            className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
                          >
                            <i className="fas fa-file-csv mr-2 text-green-600"></i>
                            Exporter les entreprises
                          </button>
                          <button
                            onClick={handleExportUsers}
                            disabled={exporting}
                            className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
                          >
                            <i className="fas fa-file-csv mr-2 text-green-600"></i>
                            Exporter les utilisateurs
                          </button>
                          <button
                            onClick={handleExportDeclarations}
                            disabled={exporting}
                            className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
                          >
                            <i className="fas fa-file-csv mr-2 text-green-600"></i>
                            Exporter les déclarations
                          </button>
                          <button
                            onClick={handleDownloadTemplate}
                            className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
                          >
                            <i className="fas fa-file-download mr-2 text-purple-600"></i>
                            Télécharger le modèle CSV
                          </button>
                        </div>
                      </div>
                      
                      <div className="border rounded-lg p-6">
                        <h4 className="font-semibold mb-4 flex items-center">
                          <i className="fas fa-info-circle mr-2 text-blue-600"></i>
                          Informations
                        </h4>
                        <div className="space-y-3 text-sm text-gray-600">
                          <p>
                            <i className="fas fa-check-circle mr-2 text-green-600"></i>
                            Les exports sont générés au format CSV
                          </p>
                          <p>
                            <i className="fas fa-check-circle mr-2 text-green-600"></i>
                            Les filtres actifs sont appliqués aux exports
                          </p>
                          <p>
                            <i className="fas fa-check-circle mr-2 text-green-600"></i>
                            Le modèle CSV peut être utilisé pour l'import de déclarations
                          </p>
                          <p>
                            <i className="fas fa-info-circle mr-2 text-blue-600"></i>
                            Les rapports PDF sont disponibles dans la section Déclarations
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-600">
          <p>
            Made by <a href="https://sayele.co" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">SAYELE</a>
          </p>
        </div>
      </footer>

      {/* Add Company Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                <i className="fas fa-building mr-2"></i>
                Nouvelle Entreprise
              </h3>
              <button onClick={() => setShowCompanyModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="fas fa-times text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NIF *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.nif}
                    onChange={(e) => setNewCompany({...newCompany, nif: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secteur *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.sector}
                    onChange={(e) => setNewCompany({...newCompany, sector: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="Mines">Mines</option>
                    <option value="Agriculture">Agriculture</option>
                    <option value="Industrie">Industrie</option>
                    <option value="Services">Services</option>
                    <option value="Commerce">Commerce</option>
                    <option value="BTP">BTP</option>
                    <option value="Télécommunications">Télécommunications</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forme Juridique</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.legal_form}
                    onChange={(e) => setNewCompany({...newCompany, legal_form: e.target.value})}
                  >
                    <option value="SA">SA - Société Anonyme</option>
                    <option value="SARL">SARL - Société à Responsabilité Limitée</option>
                    <option value="SNC">SNC - Société en Nom Collectif</option>
                    <option value="SCS">SCS - Société en Commandite Simple</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.email}
                    onChange={(e) => setNewCompany({...newCompany, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.phone}
                    onChange={(e) => setNewCompany({...newCompany, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Registre Commerce</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.registration_number}
                    onChange={(e) => setNewCompany({...newCompany, registration_number: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'employés</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.employee_count}
                    onChange={(e) => setNewCompany({...newCompany, employee_count: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={newCompany.address}
                  onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCompanyModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <i className="fas fa-save mr-2"></i>
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                <i className="fas fa-edit mr-2"></i>
                Modifier l'Entreprise
              </h3>
              <button onClick={() => {
                setShowEditCompanyModal(false);
                setEditingCompany(null);
              }} className="text-gray-500 hover:text-gray-700">
                <i className="fas fa-times text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleUpdateCompany} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NIF *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.nif}
                    onChange={(e) => setNewCompany({...newCompany, nif: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secteur *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.sector}
                    onChange={(e) => setNewCompany({...newCompany, sector: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="Mines">Mines</option>
                    <option value="Agriculture">Agriculture</option>
                    <option value="Industrie">Industrie</option>
                    <option value="Services">Services</option>
                    <option value="Commerce">Commerce</option>
                    <option value="BTP">BTP</option>
                    <option value="Télécommunications">Télécommunications</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forme Juridique</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.legal_form}
                    onChange={(e) => setNewCompany({...newCompany, legal_form: e.target.value})}
                  >
                    <option value="SA">SA - Société Anonyme</option>
                    <option value="SARL">SARL - Société à Responsabilité Limitée</option>
                    <option value="SNC">SNC - Société en Nom Collectif</option>
                    <option value="SCS">SCS - Société en Commandite Simple</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.email}
                    onChange={(e) => setNewCompany({...newCompany, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.phone}
                    onChange={(e) => setNewCompany({...newCompany, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Registre Commerce</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.registration_number}
                    onChange={(e) => setNewCompany({...newCompany, registration_number: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'employés</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newCompany.employee_count}
                    onChange={(e) => setNewCompany({...newCompany, employee_count: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={newCompany.address}
                  onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditCompanyModal(false);
                    setEditingCompany(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <i className="fas fa-save mr-2"></i>
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                <i className="fas fa-user-plus mr-2"></i>
                Nouvel Utilisateur
              </h3>
              <button onClick={() => setShowUserModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="fas fa-times text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.tenant_id}
                    onChange={(e) => setNewUser({...newUser, tenant_id: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    {companies.map((company: any) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    <option value="admin">Admin</option>
                    <option value="contributor">Contributeur</option>
                    <option value="reader">Lecteur</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <i className="fas fa-save mr-2"></i>
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                <i className="fas fa-user-edit mr-2"></i>
                Modifier l'Utilisateur
              </h3>
              <button onClick={() => {
                setShowEditUserModal(false);
                setEditingUser(null);
              }} className="text-gray-500 hover:text-gray-700">
                <i className="fas fa-times text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.tenant_id}
                    onChange={(e) => setNewUser({...newUser, tenant_id: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    {companies.map((company: any) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    <option value="admin">Admin</option>
                    <option value="contributor">Contributeur</option>
                    <option value="reader">Lecteur</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nouveau mot de passe (laisser vide pour ne pas modifier)
                  </label>
                  <input
                    type="password"
                    minLength={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder="Laisser vide si inchangé"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditUserModal(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <i className="fas fa-save mr-2"></i>
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Declaration Details Modal */}
      {showDeclarationModal && viewingDeclaration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">
                <i className="fas fa-file-alt mr-2 text-blue-600"></i>
                Détails de la Déclaration - {getCompanyName(viewingDeclaration.tenant_id)} ({viewingDeclaration.year})
              </h3>
              <button
                onClick={() => {
                  setShowDeclarationModal(false);
                  setViewingDeclaration(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            <div className="p-6">
              {/* Status Badge */}
              <div className="mb-6 flex items-center justify-between">
                <span className={`px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(viewingDeclaration.status)}`}>
                  {getStatusLabel(viewingDeclaration.status)}
                </span>
                <button
                  onClick={() => handleGeneratePDF(viewingDeclaration)}
                  disabled={generatingPDF}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                >
                  <i className="fas fa-file-pdf mr-2"></i>
                  {generatingPDF ? 'Génération...' : 'Télécharger PDF'}
                </button>
              </div>

              {/* Section: Effectifs */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                  <i className="fas fa-users mr-2 text-blue-600"></i>
                  Effectifs
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Employés permanents</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.permanent_employees || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Employés temporaires</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.temporary_employees || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Hommes</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.male_employees || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Femmes</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.female_employees || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Contrats CDI</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.cdi_contracts || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Contrats CDD</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.cdd_contracts || 0}</p>
                  </div>
                </div>
              </div>

              {/* Section: Masse Salariale */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                  <i className="fas fa-coins mr-2 text-green-600"></i>
                  Masse Salariale
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Masse salariale totale</p>
                    <p className="text-lg font-semibold">{(viewingDeclaration.total_payroll || 0).toLocaleString()} GNF</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Salaire moyen</p>
                    <p className="text-lg font-semibold">{(viewingDeclaration.average_salary || 0).toLocaleString()} GNF</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Salaire minimum</p>
                    <p className="text-lg font-semibold">{(viewingDeclaration.min_salary || 0).toLocaleString()} GNF</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Salaire maximum</p>
                    <p className="text-lg font-semibold">{(viewingDeclaration.max_salary || 0).toLocaleString()} GNF</p>
                  </div>
                </div>
              </div>

              {/* Section: Formation */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                  <i className="fas fa-graduation-cap mr-2 text-purple-600"></i>
                  Formation Professionnelle
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Heures de formation</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.training_hours || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Participants</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.training_participants || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Budget formation</p>
                    <p className="text-lg font-semibold">{(viewingDeclaration.training_budget || 0).toLocaleString()} GNF</p>
                  </div>
                </div>
                {viewingDeclaration.training_topics && (
                  <div className="mt-3 bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Thèmes de formation</p>
                    <p className="text-sm">{viewingDeclaration.training_topics}</p>
                  </div>
                )}
                {viewingDeclaration.training_providers && (
                  <div className="mt-3 bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Prestataires</p>
                    <p className="text-sm">{viewingDeclaration.training_providers}</p>
                  </div>
                )}
              </div>

              {/* Section: Santé et Sécurité */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                  <i className="fas fa-shield-alt mr-2 text-red-600"></i>
                  Santé et Sécurité
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Accidents de travail</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.work_accidents || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Jours d'arrêt maladie</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.sick_leave_days || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Heures formation sécurité</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.safety_training_hours || 0}</p>
                  </div>
                </div>
              </div>

              {/* Section: Relations Sociales */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                  <i className="fas fa-handshake mr-2 text-orange-600"></i>
                  Relations Sociales
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Représentants syndicaux</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.union_reps || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Accords collectifs</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.collective_agreements || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Nombre de réunions</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.meetings_count || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Conflits sociaux</p>
                    <p className="text-lg font-semibold">{viewingDeclaration.social_conflicts || 0}</p>
                  </div>
                </div>
              </div>

              {/* Section: Dates */}
              <div className="bg-gray-50 p-4 rounded">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Date de création</p>
                    <p className="font-medium">{formatDate(viewingDeclaration.created_at)}</p>
                  </div>
                  {viewingDeclaration.submitted_at && (
                    <div>
                      <p className="text-gray-500">Date de soumission</p>
                      <p className="font-medium">{formatDate(viewingDeclaration.submitted_at)}</p>
                    </div>
                  )}
                  {viewingDeclaration.validated_at && (
                    <div>
                      <p className="text-gray-500">Date de validation</p>
                      <p className="font-medium">{formatDate(viewingDeclaration.validated_at)}</p>
                    </div>
                  )}
                  {viewingDeclaration.rejection_reason && (
                    <div className="col-span-2">
                      <p className="text-red-500 font-medium">Raison du rejet</p>
                      <p className="text-red-700">{viewingDeclaration.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeclarationModal(false);
                    setViewingDeclaration(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardBCEIP;
